/** @jsxImportSource react */

import { useShare } from '@common/helpers/share';
import useSearchInsights from '@react/data-hub/hooks/useSearchInsights';
import { InsightsFilters } from '@react/data-hub/types/misc';
import { formatMoney } from '@sly/core/src/helpers/numbers';
import Link from 'next/link';
import { useRouter } from 'next/router';

import CommonToolForm from './CommonToolForm';
import { ToolHero } from './Styles';
import { defaultSearchInsightsFilter } from '../constants/data';
import { ToolDetails } from '../types';

export default function NearbyCitiesTool({
  heading,
  description,
  shortDescription,
}: ToolDetails) {
  const { query, push, pathname } = useRouter();
  const { city, state, toolName } = query || {};
  const { searchInsights, locationStringWithCountryCode, locationString } =
    useSearchInsights({
      filters: {
        toc: defaultSearchInsightsFilter.careType,
        city,
        state,
        roomType: defaultSearchInsightsFilter.roomType,
        include: defaultSearchInsightsFilter.include,
      } as InsightsFilters,
    });

  const { share } = useShare();

  const nearbyEntityInfo = searchInsights?.info?.nearbyEntityInfo;

  const handleSubmit = (values: any) => {
    const currentFilters = {
      state: '',
      city: '',
      toolName,
    };
    const searchParams = values?.location?.searchParams;

    if (searchParams?.state) {
      currentFilters.state = searchParams.state;
    }
    if (searchParams?.city) {
      currentFilters.city = searchParams.city;
    }

    if (!currentFilters.city || !currentFilters.state) {
      return;
    }

    push(
      {
        pathname,
        query: currentFilters,
      },
      undefined,
      { shallow: true }
    );
  };

  return (
    <>
      <ToolHero
        heading={heading}
        subheading={description}
        onShare={() => {
          share({
            title: heading,
            text: shortDescription || description || '',
            url: window.location.href,
          });
        }}
      />

      <div className='space-y-6'>
        <div>
          <CommonToolForm
            initialFilters={{
              location: {
                displayText: locationStringWithCountryCode,
                searchParams: {
                  state,
                  city,
                },
              },
            }}
            handleSubmit={handleSubmit}
            locationString={locationStringWithCountryCode}
            nearest={searchInsights?.nearest}
            className='px-0'
            eventProps={{ location: `${heading} tool` }}
          />
        </div>

        {nearbyEntityInfo && (
          <div className='rounded-lg border border-slate-lighter-95 p-8'>
            <div className='flex flex-col gap-8 md:flex-row'>
              <div className='space-y-6 md:w-1/2'>
                <div className='space-y-1'>
                  <h3 className='font-b-s'>
                    Average monthly cost for {locationString}
                  </h3>
                  <p className='font-t-m-azo'>{formatMoney(12000)}/mo</p>
                </div>
                <p className='pr-8'>
                  The figures show the average monthly starting costs for{' '}
                  <span className='lowercase'>{searchInsights?.careType}</span>{' '}
                  of nearby cities. Keep in mind that these figures are an
                  average, and that the numbers used to calculate it spanned a
                  wide range. In fact, Seniorly data suggests that most families
                  will underestimate their monthly budget by almost 20%, meaning
                  that once inclusions and services are factored in, they can
                  actually afford more than they originally thought.
                </p>
              </div>
              <div className='space-y-4 md:w-1/2'>
                {nearbyEntityInfo
                  .filter(({ averagePrice }: any) => averagePrice > 0)
                  .map(({ name, averagePrice, url }: any) => (
                    <div
                      key={name}
                      className='grid grid-cols-2 items-center justify-between gap-6'
                    >
                      <Link href={url}>
                        <span className='text-viridian-base'>{name}</span>
                      </Link>
                      <span>{formatMoney(averagePrice)}/mo</span>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
